<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { formatNumber } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, boolean, number, object, string } from 'yup';

const { tenant, competitor, competitorSeoTypes, competitorSeaTypes, targets } =
    defineProps({
        tenant: {
            required: true,
            type: Object,
        },
        competitor: {
            required: false,
            type: Object,
        },
        competitorSeoTypes: {
            required: true,
            type: Array,
        },
        competitorSeaTypes: {
            required: true,
            type: Array,
        },
        targets: {
            required: false,
            type: Array,
        },
    });

const { route } = useRoute();

const form = reactive({
    name: competitor ? competitor.name : '',
    description: competitor ? competitor.description : '',
    url: competitor ? competitor.url : '',
    includedKeywords:
        competitor && competitor.includedKeywords
            ? competitor.includedKeywords.join('\n')
            : '',
    excludedKeywords:
        competitor && competitor.excludedKeywords
            ? competitor.excludedKeywords.join('\n')
            : '',
    seoEnabled: competitor ? competitor.seoEnabled : true,
    seoType: competitor
        ? competitorSeoTypes.find(
              (type) => type.value === competitor.seoType.value,
          )
        : competitorSeoTypes[0],
    seoKeywordLimit: competitor ? competitor.seoKeywordLimit : 1000,
    seaEnabled: competitor ? competitor.seaEnabled : true,
    seaType: competitor
        ? competitorSeaTypes.find(
              (type) => type.value === competitor.seaType.value,
          )
        : competitorSeaTypes[0],
    seaKeywordLimit: competitor ? competitor.seaKeywordLimit : 1000,
    smaMetaUrl: competitor ? competitor.smaMetaUrl : '',
    targets: targets.reduce((acc, target) => {
        if (competitor) {
            let values = [];

            competitor.competitorTargets.forEach((competitorTarget) => {
                if (
                    competitorTarget.websiteVariant.id ===
                    target.websiteVariant.id
                ) {
                    values.push(
                        ...target.googleWebsites.filter(
                            (googleWebsite) =>
                                googleWebsite.id ===
                                competitorTarget.googleWebsite.id,
                        ),
                    );
                }
            });

            acc[target.websiteVariant.id] = values;
        } else {
            acc[target.websiteVariant.id] = [];
        }

        return acc;
    }, {}),
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est requis'),
    description: string().nullable(),
    url: string().url("L'url est invalide").required('Ce champ est requis'),
    includedKeywords: string().nullable(),
    excludedKeywords: string().nullable(),
    seoEnabled: boolean().required('Ce champ est requis'),
    seoType: object().required('Ce champ est requis'),
    seoKeywordLimit: number()
        .required('Ce champ est requis')
        .min(1, 'La valeur minimale est {min}')
        .max(1000, 'La valeur maximale est 1000'),
    seaEnabled: boolean().required('Ce champ est requis'),
    seaType: object().required('Ce champ est requis'),
    seaKeywordLimit: number()
        .required('Ce champ est requis')
        .min(1, 'La valeur minimale est {min}')
        .max(1000, 'La valeur maximale est 1000'),
    smaMetaUrl: string().nullable(),
    targets: array().of(
        array().test(
            'at-least-one-target',
            'Au moins une cible est requise',
            () => {
                return Object.values(form.targets).some(
                    (targetArray) => targetArray.length > 0,
                );
            },
        ),
    ),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postCompetitor', {
                tenant: tenant.id,
                ...(competitor ? { id: competitor.id } : {}),
            }),
            {
                name: form.name,
                description: form.description,
                url: form.url,
                includedKeywords: form.includedKeywords
                    ? form.includedKeywords
                          .split('\n')
                          .map((keyword) => keyword.trim())
                          .filter((keyword) => keyword.length > 0)
                    : [],
                excludedKeywords: form.excludedKeywords
                    ? form.excludedKeywords
                          .split('\n')
                          .map((keyword) => keyword.trim())
                          .filter((keyword) => keyword.length > 0)
                    : [],
                seoEnabled: form.seoEnabled,
                seoType: form.seoType.value,
                seoKeywordLimit: form.seoKeywordLimit,
                seaEnabled: form.seaEnabled,
                seaType: form.seaType.value,
                seaKeywordLimit: form.seaKeywordLimit,
                smaMetaUrl: form.smaMetaUrl,
                targets: Object.fromEntries(
                    Object.entries(form.targets).map(([key, value]) => [
                        key,
                        value.map((item) => item.id),
                    ]),
                ),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.modules.competitors',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.competitors', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />

                    <InputField
                        v-model="form.url"
                        label="URL"
                        field-name="url"
                        field-id="url"
                        type="url"
                        required
                    />
                </FormGrid>

                <EditorField
                    v-model="form.description"
                    label="Description"
                    field-name="description"
                    field-id="description"
                    size="small"
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <TextareaField
                        v-model="form.includedKeywords"
                        label="Mots clés inclus"
                        field-name="includedKeywords"
                        field-id="includedKeywords"
                        legend="Un mot clé par ligne"
                        :rows="25"
                        resize="none"
                    />

                    <TextareaField
                        v-model="form.excludedKeywords"
                        label="Mots clés exclus"
                        field-name="excludedKeywords"
                        field-id="excludedKeywords"
                        legend="Un mot clé par ligne"
                        :rows="25"
                        resize="none"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <SwitchField
                        v-model="form.seoEnabled"
                        field-name="seoEnabled"
                        field-id="seoEnabled"
                        label="SEO activé"
                    />
                </FormGrid>

                <FormGrid>
                    <ListboxField
                        v-model="form.seoType"
                        field-id="seoType"
                        field-name="seoType"
                        label="Type SEO"
                        :options="competitorSeoTypes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.seoType.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.seoKeywordLimit"
                        label="Limite de mots clés SEO"
                        field-name="seoKeywordLimit"
                        field-id="seoKeywordLimit"
                        type="number"
                        :legend="
                            '0.01$ tous les 1000 mots clés + 0.0001$ par mot clé - ' +
                            form.seoKeywordLimit +
                            ' mot(s) clé(s) = ' +
                            formatNumber(
                                Math.ceil(form.seoKeywordLimit / 1000) * 0.01 +
                                    form.seoKeywordLimit * 0.0001,
                            ) +
                            '$'
                        "
                        :max="1000"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <SwitchField
                        v-model="form.seaEnabled"
                        field-name="seaEnabled"
                        field-id="seaEnabled"
                        label="SEA activé"
                    />
                </FormGrid>

                <FormGrid>
                    <ListboxField
                        v-model="form.seaType"
                        field-id="seaType"
                        field-name="seaType"
                        label="Type SEA"
                        :options="competitorSeaTypes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.seaType.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.seaKeywordLimit"
                        label="Limite de mots clés SEA"
                        field-name="seaKeywordLimit"
                        field-id="seaKeywordLimit"
                        type="number"
                        :legend="
                            '0.01$ tous les 1000 mots clés + 0.0001$ par mot clé - ' +
                            form.seaKeywordLimit +
                            ' mot(s) clé(s) = ' +
                            formatNumber(
                                Math.ceil(form.seaKeywordLimit / 1000) * 0.01 +
                                    form.seaKeywordLimit * 0.0001,
                            ) +
                            '$'
                        "
                        :max="1000"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.smaMetaUrl"
                        label="URL Meta SMA"
                        field-name="smaMetaUrl"
                        field-id="smaMetaUrl"
                        type="url"
                        legend="https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=FR&is_targeted_country=false&media_type=all&search_type=page&view_all_page_id=PAGE_ID"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <ComboboxField
                        v-for="(target, index) in targets"
                        :key="index"
                        v-model="form.targets[target.websiteVariant.id]"
                        filtered-option-key="name"
                        has-empty-option
                        :options="target.googleWebsites"
                        :multiple="true"
                        :field-name="
                            'targets[' + target.websiteVariant.id + ']'
                        "
                        :field-id="'targets[' + target.websiteVariant.id + ']'"
                        :label="'Cibles (' + target.websiteVariant.name + ')'"
                        by="id"
                    >
                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ComboboxField>
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="competitor"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteCompetitor',
                            {
                                tenant: tenant.id,
                                id: competitor.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.competitors',
                                    { tenant: tenant.id },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
